import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";

/* ─────────────────────────────────────────
   BACKGROUND — exact Pinterest image
───────────────────────────────────────── */
const BG_IMAGE =
  "https://i.pinimg.com/1200x/c5/b8/fa/c5b8fa480ab8a114979eb594c42d9ec5.jpg";

/* ─────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────── */
const T = {
  gold:        "#C9A84C",
  goldLight:   "#EDD06A",
  goldDim:     "rgba(201,168,76,0.55)",
  goldBorder:  "rgba(201,168,76,0.22)",
  goldGlow:    "rgba(201,168,76,0.35)",
  goldFaint:   "rgba(201,168,76,0.08)",
  burg:        "#5B0E14",
  burgLight:   "#7A1520",
  cream:       "#F5EDD8",
  creamFade:   "rgba(245,237,216,0.60)",
  creamDim:    "rgba(245,237,216,0.30)",
  creamGhost:  "rgba(245,237,216,0.12)",
  glass:       "rgba(6,2,1,0.72)",
  glassDark:   "rgba(4,1,0,0.85)",
  green:       "#6ee7b7",
  greenBg:     "rgba(20,60,38,0.65)",
  greenBorder: "rgba(110,231,183,0.28)",
  red:         "#fca5a5",
  redBg:       "rgba(80,14,14,0.60)",
  redBorder:   "rgba(252,165,165,0.22)",
};

/* ─────────────────────────────────────────
   KEYFRAMES (injected once)
───────────────────────────────────────── */
const GLOBAL_CSS = `
  @keyframes mgFadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes mgFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes mgSlideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes mgShimmer {
    0%,100% { opacity: 0.45; }
    50%      { opacity: 1; }
  }
  @keyframes mgPulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(201,168,76,0); }
    50%      { box-shadow: 0 0 18px 4px rgba(201,168,76,0.22); }
  }
  @keyframes mgSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes mgGlow {
    0%,100% { text-shadow: 0 0 18px rgba(201,168,76,0.25); }
    50%      { text-shadow: 0 0 36px rgba(201,168,76,0.55), 0 0 72px rgba(201,168,76,0.18); }
  }
  @keyframes mgBadgePop {
    0%   { transform: scale(0.7); opacity: 0; }
    70%  { transform: scale(1.08); }
    100% { transform: scale(1); opacity: 1; }
  }

  .mg-card {
    transition: transform 0.22s cubic-bezier(0.22,1,0.36,1),
                box-shadow 0.22s cubic-bezier(0.22,1,0.36,1),
                border-color 0.22s ease;
  }
  .mg-card:hover {
    transform: translateY(-4px) scale(1.005);
    box-shadow: 0 20px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(201,168,76,0.18), 0 0 40px rgba(201,168,76,0.06) !important;
    border-color: rgba(201,168,76,0.35) !important;
  }
  .mg-btn-resume {
    transition: filter 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  }
  .mg-btn-resume:hover {
    filter: brightness(1.12);
    transform: translateY(-1px);
    box-shadow: 0 8px 28px rgba(201,168,76,0.35) !important;
  }
  .mg-btn-resume:active { transform: scale(0.97); }

  .mg-tab {
    transition: color 0.18s ease, border-color 0.18s ease;
  }

  .mg-btn-delete {
    transition: background 0.18s ease, border-color 0.18s ease, transform 0.15s ease;
  }
  .mg-btn-delete:hover { transform: scale(1.08); }

  .mg-chip {
    transition: background 0.18s ease, border-color 0.18s ease;
  }
  .mg-chip:hover {
    background: rgba(201,168,76,0.14) !important;
    border-color: rgba(201,168,76,0.35) !important;
  }
`;

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "الآن";
  if (mins < 60) return `قبل ${mins} دقيقة`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `قبل ${hrs} ساعة`;
  const days = Math.floor(hrs / 24);
  return `قبل ${days} يوم`;
}

function progressPercent(save) {
  const total = (save.categories?.length || 0) * 6;
  if (total === 0) return 0;
  return Math.round(((save.used_tiles?.length || 0) / total) * 100);
}

function formatDate(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleDateString("ar-SA", {
    day: "numeric", month: "long", year: "numeric",
  });
}

/* ─────────────────────────────────────────
   STATUS CONFIG
───────────────────────────────────────── */
const STATUS = {
  active:    { label: "● جارية",   color: T.green,   bg: "rgba(20,70,44,0.55)", border: T.greenBorder },
  paused:    { label: "◌ محفوظة",  color: T.gold,    bg: "rgba(50,34,6,0.55)",  border: "rgba(201,168,76,0.30)" },
  completed: { label: "✦ مكتملة",  color: T.creamFade, bg: "rgba(30,20,10,0.55)", border: "rgba(245,237,216,0.18)" },
};

/* ─────────────────────────────────────────
   SCORE DISPLAY
───────────────────────────────────────── */
function Score({ value, isWinner, isCompleted }) {
  return (
    <div style={{
      fontSize:   isWinner && isCompleted ? "2.4rem" : "2rem",
      fontWeight: 900,
      fontFamily: "'Cairo', sans-serif",
      color:      isWinner && isCompleted ? T.goldLight : T.cream,
      lineHeight: 1,
      textShadow: isWinner && isCompleted
        ? `0 0 30px ${T.goldGlow}, 0 2px 0 rgba(0,0,0,0.5)`
        : "0 2px 0 rgba(0,0,0,0.4)",
      transition: "all 0.3s ease",
    }}>
      {value}
    </div>
  );
}

/* ─────────────────────────────────────────
   DELETE CONFIRM OVERLAY
───────────────────────────────────────── */
function DeleteOverlay({ onConfirm, onCancel }) {
  return (
    <div style={{
      position: "absolute", inset: 0, borderRadius: "inherit",
      background: "rgba(4,1,0,0.96)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: "20px", padding: "32px",
      zIndex: 10,
      animation: "mgFadeIn 0.18s ease",
    }}>
      <div style={{ fontSize: "2rem" }}>🗑</div>
      <div style={{ textAlign: "center" }}>
        <div style={{ color: T.cream, fontFamily: "Cairo,sans-serif", fontWeight: 800, fontSize: "1rem", marginBottom: "6px" }}>
          تحذف المباراة؟
        </div>
        <div style={{ color: T.creamDim, fontSize: "0.78rem" }}>
          ما راح تقدر تكملها بعد الحذف.
        </div>
      </div>
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          onClick={onConfirm}
          className="mg-btn-delete"
          style={{
            padding: "10px 26px", borderRadius: "12px",
            background: T.redBg, border: `1.5px solid ${T.redBorder}`,
            color: T.red, fontFamily: "Cairo,sans-serif",
            fontWeight: 800, fontSize: "0.85rem", cursor: "pointer",
          }}
        >نعم، احذف</button>
        <button
          onClick={onCancel}
          className="mg-btn-delete"
          style={{
            padding: "10px 26px", borderRadius: "12px",
            background: T.goldFaint, border: `1.5px solid ${T.goldBorder}`,
            color: T.creamFade, fontFamily: "Cairo,sans-serif",
            fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
          }}
        >إلغاء</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   GAME CARD
───────────────────────────────────────── */
function GameCard({ save, onResume, onDelete, isResuming, index }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const pct        = progressPercent(save);
  const isActive   = save.status === "active" || save.status === "paused";
  const isCompleted = save.status === "completed";
  const st         = STATUS[save.status] || STATUS.paused;
  const isSolo     = save.game_mode === "solo";

  const t1Wins = save.team1_score > save.team2_score;
  const t2Wins = save.team2_score > save.team1_score;
  const isDraw = save.team1_score === save.team2_score;

  // SOLO-specific computed values
  const soloTarget  = save.solo_target || 8000;
  const soloScore   = save.team1_score || 0;
  const soloWon     = isSolo && soloScore >= soloTarget;
  const soloPlayer  = save.solo_player_name || save.team1_name || "—";
  const soloDiffLabel = save.solo_difficulty === "easy" ? "سهل" : save.solo_difficulty === "hard" ? "صعب" : "متوسط";

  return (
    <div
      className="mg-card"
      style={{
        position: "relative",
        borderRadius: "20px",
        background: T.glass,
        border: `1.5px solid ${isActive ? "rgba(201,168,76,0.28)" : "rgba(201,168,76,0.13)"}`,
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        overflow: "hidden",
        boxShadow: isActive
          ? "0 12px 48px rgba(0,0,0,0.60), 0 0 0 1px rgba(201,168,76,0.06)"
          : "0 6px 28px rgba(0,0,0,0.50)",
        animation: `mgFadeUp 0.4s cubic-bezier(0.22,1,0.36,1) ${index * 0.07}s both`,
      }}
    >
      {/* Shimmer line at top */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "1px",
        background: `linear-gradient(90deg, transparent 0%, ${isActive ? "rgba(201,168,76,0.50)" : "rgba(201,168,76,0.18)"} 50%, transparent 100%)`,
        pointerEvents: "none",
      }} />

      {/* Inner content */}
      <div style={{ padding: "22px 24px 20px", display: "flex", flexDirection: "column", gap: "18px" }}>

        {/* ── Header row: status + winner/draw badge ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Status badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "4px 12px", borderRadius: "999px",
            background: st.bg, border: `1px solid ${st.border}`,
            color: st.color, fontSize: "0.65rem", fontWeight: 800,
            fontFamily: "Cairo,sans-serif", letterSpacing: "0.04em",
            animation: "mgBadgePop 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
          }}>
            {st.label}
          </div>

          {/* SOLO badge */}
          {isSolo && (
            <div style={{
              padding: "4px 12px", borderRadius: "999px",
              background: "rgba(30,60,160,0.25)",
              border: "1px solid rgba(147,197,253,0.28)",
              color: "rgba(147,197,253,0.90)",
              fontSize: "0.65rem", fontWeight: 800,
              fontFamily: "Cairo,sans-serif",
              animation: "mgBadgePop 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
            }}>
              🎯 SOLO — {soloDiffLabel}
            </div>
          )}

          {/* Winner / draw (completed, standard only) */}
          {isCompleted && !isSolo && (
            <div style={{
              padding: "4px 14px", borderRadius: "999px",
              background: isDraw
                ? "rgba(255,255,255,0.06)"
                : "rgba(201,168,76,0.12)",
              border: `1px solid ${isDraw ? "rgba(255,255,255,0.12)" : "rgba(201,168,76,0.28)"}`,
              color: isDraw ? T.creamFade : T.goldLight,
              fontSize: "0.65rem", fontWeight: 800,
              fontFamily: "Cairo,sans-serif",
              animation: "mgBadgePop 0.4s 0.1s cubic-bezier(0.34,1.56,0.64,1) both",
            }}>
              {isDraw ? "🤝 تعادل" : `🏆 ${t1Wins ? save.team1_name : save.team2_name}`}
            </div>
          )}

          {/* SOLO completed: win/fail badge */}
          {isCompleted && isSolo && (
            <div style={{
              padding: "4px 14px", borderRadius: "999px",
              background: soloWon ? "rgba(20,80,40,0.40)" : "rgba(80,20,20,0.30)",
              border: `1px solid ${soloWon ? "rgba(74,222,128,0.35)" : "rgba(248,113,113,0.25)"}`,
              color: soloWon ? "#4ade80" : "#fca5a5",
              fontSize: "0.65rem", fontWeight: 800,
              fontFamily: "Cairo,sans-serif",
              animation: "mgBadgePop 0.4s 0.1s cubic-bezier(0.34,1.56,0.64,1) both",
            }}>
              {soloWon ? "🎯 تحقّق الهدف" : "لم يتحقق الهدف"}
            </div>
          )}

          {/* Time ago (active) */}
          {isActive && (
            <div style={{ color: T.creamDim, fontSize: "0.62rem", fontFamily: "Cairo,sans-serif" }}>
              {timeAgo(save.last_save_at)}
            </div>
          )}
        </div>

        {/* ── Scoreboard ── */}
        {isSolo ? (
          /* SOLO scoreboard: player score vs target */
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ flex: 1 }}>
              <div style={{
                color: "rgba(147,197,253,0.85)", fontSize: "0.80rem", fontWeight: 700,
                fontFamily: "Cairo,sans-serif", marginBottom: "6px",
                display: "flex", alignItems: "center", gap: "5px",
              }}>
                <span>🎮</span>
                <span style={{ maxWidth: "90px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {soloPlayer}
                </span>
              </div>
              <Score value={soloScore} isWinner={soloWon} isCompleted={isCompleted} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px", flexShrink: 0 }}>
              <div style={{
                fontSize: "0.60rem", fontWeight: 700, color: "rgba(147,197,253,0.50)",
                fontFamily: "Cairo,sans-serif", padding: "2px 8px",
                border: "1px solid rgba(147,197,253,0.15)", borderRadius: "6px",
                background: "rgba(147,197,253,0.05)",
              }}>الهدف</div>
              <div style={{ fontSize: "1rem", fontWeight: 900, color: soloWon ? "#4ade80" : T.goldLight, fontFamily: "Cairo,sans-serif" }}>
                {soloTarget.toLocaleString()}
              </div>
            </div>
          </div>
        ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>

          {/* Team 1 */}
          <div style={{ flex: 1, textAlign: "right" }}>
            <div style={{
              color: t1Wins && isCompleted ? T.goldLight : "rgba(245,237,216,0.88)",
              fontSize: "0.80rem", fontWeight: 700,
              fontFamily: "Cairo,sans-serif", marginBottom: "6px",
              display: "flex", alignItems: "center", gap: "5px", justifyContent: "flex-end",
            }}>
              <span style={{ fontSize: "1rem" }}>🦁</span>
              <span style={{
                maxWidth: "90px", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{save.team1_name}</span>
            </div>
            <Score value={save.team1_score} isWinner={t1Wins} isCompleted={isCompleted} />
          </div>

          {/* VS divider */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", flexShrink: 0 }}>
            <div style={{
              width: "1px", height: "20px",
              background: "linear-gradient(to bottom, transparent, rgba(201,168,76,0.25), transparent)",
            }} />
            <div style={{
              fontSize: "0.68rem", fontWeight: 900, letterSpacing: "0.12em",
              color: "rgba(201,168,76,0.30)", fontFamily: "Cairo,sans-serif",
              padding: "2px 8px", border: `1px solid rgba(201,168,76,0.12)`,
              borderRadius: "6px", background: "rgba(201,168,76,0.04)",
            }}>VS</div>
            <div style={{
              width: "1px", height: "20px",
              background: "linear-gradient(to bottom, transparent, rgba(201,168,76,0.25), transparent)",
            }} />
          </div>

          {/* Team 2 */}
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{
              color: t2Wins && isCompleted ? T.goldLight : "rgba(245,237,216,0.88)",
              fontSize: "0.80rem", fontWeight: 700,
              fontFamily: "Cairo,sans-serif", marginBottom: "6px",
              display: "flex", alignItems: "center", gap: "5px", justifyContent: "flex-start",
            }}>
              <span style={{
                maxWidth: "90px", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{save.team2_name}</span>
              <span style={{ fontSize: "1rem" }}>🦅</span>
            </div>
            <Score value={save.team2_score} isWinner={t2Wins} isCompleted={isCompleted} />
          </div>
        </div>
        )}

        {/* ── Progress bar (active/paused) ── */}
        {isActive && (
          <div>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: "7px",
            }}>
              <span style={{ color: T.creamDim, fontSize: "0.61rem", fontFamily: "Cairo,sans-serif" }}>
                {save.used_tiles?.length || 0} / {(save.categories?.length || 0) * 6} سؤال
              </span>
              <span style={{ color: T.gold, fontSize: "0.61rem", fontWeight: 800 }}>
                {pct}%
              </span>
            </div>
            <div style={{
              height: "3px", borderRadius: "99px",
              background: "rgba(255,255,255,0.07)",
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%", borderRadius: "99px",
                width: `${pct}%`,
                background: `linear-gradient(90deg, rgba(91,14,20,0.8), ${T.gold}, ${T.goldLight})`,
                boxShadow: `0 0 10px ${T.goldGlow}`,
                transition: "width 0.6s cubic-bezier(0.22,1,0.36,1)",
              }} />
            </div>
          </div>
        )}

        {/* ── Category chips ── */}
        {save.categories?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {save.categories.map(cat => (
              <span key={cat.id} className="mg-chip" style={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                padding: "3px 10px", borderRadius: "8px",
                background: "rgba(201,168,76,0.06)",
                border: "1px solid rgba(201,168,76,0.16)",
                color: T.creamFade, fontSize: "0.60rem", fontWeight: 600,
                fontFamily: "Cairo,sans-serif",
              }}>
                {cat.icon && <span style={{ fontSize: "0.75rem" }}>{cat.icon}</span>}
                {cat.name}
              </span>
            ))}
          </div>
        )}

        {/* ── Footer (completed: date | active: turn info) ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {isCompleted ? (
            <span style={{ color: T.creamDim, fontSize: "0.60rem", fontFamily: "Cairo,sans-serif" }}>
              {formatDate(save.last_save_at)}
            </span>
          ) : isSolo ? (
            <span style={{ color: "rgba(147,197,253,0.85)", fontSize: "0.60rem", fontWeight: 700, fontFamily: "Cairo,sans-serif" }}>
              {`🎯 ${soloScore.toLocaleString()} / ${soloTarget.toLocaleString()}`}
            </span>
          ) : (
            <span style={{ color: T.gold, fontSize: "0.60rem", fontWeight: 700, fontFamily: "Cairo,sans-serif" }}>
              {save.current_turn === 1 ? "دور 🦁" : "دور 🦅"}
            </span>
          )}
          {isActive && (
            <span style={{ color: T.creamDim, fontSize: "0.60rem", fontFamily: "Cairo,sans-serif" }}>
              {timeAgo(save.last_save_at)}
            </span>
          )}
        </div>

        {/* ── Action buttons (active only) ── */}
        {isActive && (
          <div style={{ display: "flex", gap: "8px" }}>
            {/* Resume */}
            <button
              onClick={() => onResume(save)}
              disabled={isResuming}
              className="mg-btn-resume"
              style={{
                flex: 1, padding: "13px 0", borderRadius: "14px",
                border: "none", cursor: isResuming ? "wait" : "pointer",
                background: isResuming
                  ? "rgba(30,60,40,0.40)"
                  : `linear-gradient(135deg, rgba(201,168,76,0.18) 0%, rgba(201,168,76,0.08) 100%)`,
                borderTop: `1px solid rgba(201,168,76,0.35)`,
                borderLeft: `1px solid rgba(201,168,76,0.18)`,
                borderRight: `1px solid rgba(201,168,76,0.18)`,
                borderBottom: `1px solid rgba(201,168,76,0.12)`,
                boxShadow: isResuming ? "none" : `0 4px 20px rgba(201,168,76,0.14), inset 0 1px 0 rgba(201,168,76,0.25)`,
                color: isResuming ? T.goldDim : T.goldLight,
                fontWeight: 800, fontSize: "0.88rem",
                fontFamily: "Cairo,sans-serif",
                letterSpacing: "0.02em",
                opacity: isResuming ? 0.65 : 1,
              }}
            >
              {isResuming
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ width: "12px", height: "12px", border: "2px solid rgba(201,168,76,0.3)", borderTopColor: T.gold, borderRadius: "50%", animation: "mgSpin 0.7s linear infinite", display: "inline-block" }} />
                    جاري التحميل...
                  </span>
                : "▶ استكمال المباراة"
              }
            </button>

            {/* Delete */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="mg-btn-delete"
              style={{
                width: "46px", height: "46px", borderRadius: "14px",
                border: `1.5px solid ${T.redBorder}`,
                background: T.redBg, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", color: T.red,
              }}
              title="حذف المباراة"
            >🗑</button>
          </div>
        )}
      </div>

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <DeleteOverlay
          onConfirm={() => { onDelete(save.id); setShowDeleteConfirm(false); }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   EMPTY STATE
───────────────────────────────────────── */
function EmptyState({ tab, onNew }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "64px 24px",
      animation: "mgFadeUp 0.45s ease both",
    }}>
      <div style={{
        width: "80px", height: "80px", borderRadius: "50%",
        background: "rgba(201,168,76,0.06)",
        border: `1px solid rgba(201,168,76,0.14)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "2rem", marginBottom: "20px",
        animation: "mgPulse 3s ease-in-out infinite",
      }}>
        {tab === "active" ? "🎮" : "🏆"}
      </div>
      <div style={{
        color: T.cream, fontFamily: "Cairo,sans-serif",
        fontWeight: 800, fontSize: "1.05rem", marginBottom: "8px",
      }}>
        {tab === "active" ? "لا توجد مباريات جارية" : "لا توجد مباريات مكتملة"}
      </div>
      <div style={{
        color: T.creamDim, fontSize: "0.82rem", lineHeight: 1.75,
        textAlign: "center", maxWidth: "260px", marginBottom: "28px",
      }}>
        {tab === "active"
          ? 'ابدأ مباراة واضغط «حفظ والخروج» لحفظها وإكمالها لاحقاً.'
          : 'ستظهر مبارياتك المكتملة هنا بعد إعلان الفائز.'}
      </div>
      {tab === "active" && (
        <button
          onClick={onNew}
          className="mg-btn-resume"
          style={{
            padding: "13px 40px", borderRadius: "999px",
            background: `linear-gradient(135deg, ${T.goldLight}, ${T.gold})`,
            color: "#1a0802", border: "none", cursor: "pointer",
            fontWeight: 900, fontSize: "0.92rem",
            fontFamily: "Cairo,sans-serif",
            boxShadow: `0 8px 32px ${T.goldGlow}`,
          }}
        >
          ابدأ مباراة جديدة ←
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   LOADING SKELETON
───────────────────────────────────────── */
function LoadingSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          borderRadius: "20px", height: "180px",
          background: "rgba(201,168,76,0.04)",
          border: "1.5px solid rgba(201,168,76,0.08)",
          animation: `mgShimmer 1.6s ${i * 0.2}s ease-in-out infinite`,
        }} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────
   HERO HEADER
───────────────────────────────────────── */
function HeroHeader({ username }) {
  return (
    <div style={{
      textAlign: "center",
      padding: "36px 24px 28px",
      position: "relative",
    }}>
      {/* Ambient glow behind title */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        width: "320px", height: "120px",
        background: "radial-gradient(ellipse, rgba(201,168,76,0.10) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      <div style={{
        fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.25em",
        color: T.goldDim, fontFamily: "Cairo,sans-serif",
        textTransform: "uppercase", marginBottom: "10px",
        animation: "mgFadeIn 0.6s 0.1s ease both",
      }}>
        HUJJAH ARENA
      </div>

      <h1 style={{
        margin: 0, lineHeight: 1.1,
        fontSize: "clamp(2rem, 6vw, 2.8rem)",
        fontWeight: 900, fontFamily: "Cairo,sans-serif",
        background: `linear-gradient(135deg, ${T.goldLight} 0%, ${T.gold} 45%, rgba(201,168,76,0.65) 100%)`,
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        animation: "mgGlow 3.5s ease-in-out infinite, mgFadeUp 0.55s 0.15s ease both",
      }}>
        مبارياتي
      </h1>

      {username && (
        <div style={{
          marginTop: "8px", color: T.creamDim,
          fontSize: "0.78rem", fontFamily: "Cairo,sans-serif",
          fontWeight: 600, letterSpacing: "0.03em",
          animation: "mgFadeIn 0.6s 0.3s ease both",
        }}>
          {username}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   TABS
───────────────────────────────────────── */
function Tabs({ active, onSwitch, counts }) {
  const tabs = [
    { key: "active",      label: "الجارية والمحفوظة", count: counts.active },
    { key: "completed",   label: "المكتملة",           count: counts.completed },
    { key: "tournaments", label: "البطولات",            count: counts.tournaments, icon: "🏆" },
  ];
  return (
    <div style={{
      borderBottom: "1px solid rgba(201,168,76,0.12)",
      background: "rgba(4,1,0,0.20)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
    }}>
      <div style={{
        display: "flex",
        maxWidth: "700px", margin: "0 auto",
        padding: "0 20px",
      }}>
        {tabs.map(tab => {
          const isActive = active === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onSwitch(tab.key)}
              className="mg-tab"
              style={{
                padding: "13px 16px", background: "none", border: "none",
                cursor: "pointer", fontFamily: "Cairo,sans-serif",
                fontWeight: isActive ? 800 : 600, fontSize: "0.87rem",
                color: isActive ? T.goldLight : "rgba(245,237,216,0.40)",
                borderBottom: `2px solid ${isActive ? T.gold : "transparent"}`,
                marginBottom: "-1px",
                display: "flex", alignItems: "center", gap: "6px",
              }}
            >
              {tab.icon && <span style={{ fontSize: "0.85rem" }}>{tab.icon}</span>}
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  padding: "2px 8px", borderRadius: "999px",
                  background: isActive ? "rgba(201,168,76,0.14)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${isActive ? "rgba(201,168,76,0.28)" : "rgba(255,255,255,0.07)"}`,
                  color: isActive ? T.gold : "rgba(245,237,216,0.30)",
                  fontSize: "0.58rem", fontWeight: 800,
                }}>{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   TOURNAMENT CARD
───────────────────────────────────────── */
const ROUND_NAMES = {
  4:  ["نصف النهائي", "النهائي"],
  8:  ["ربع النهائي", "نصف النهائي", "النهائي"],
  16: ["دور الـ16", "ربع النهائي", "نصف النهائي", "النهائي"],
  32: ["دور الـ32", "دور الـ16", "ربع النهائي", "نصف النهائي", "النهائي"],
};

function TournamentCard({ tournamentState, navigate }) {
  if (!tournamentState) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", animation: "mgFadeUp 0.45s ease both" }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem", marginBottom: 20, animation: "mgPulse 3s ease-in-out infinite" }}>🏆</div>
        <div style={{ color: T.cream, fontFamily: "Cairo,sans-serif", fontWeight: 800, fontSize: "1.05rem", marginBottom: 8 }}>لا توجد لديك بطولات حالياً</div>
        <div style={{ color: T.creamDim, fontSize: "0.82rem", lineHeight: 1.75, textAlign: "center", maxWidth: 260, marginBottom: 28 }}>
          انضم إلى التحدي وأقم بطولة مثيرة مع أصحابك!
        </div>
        <button
          onClick={() => navigate("/tournament")}
          className="mg-btn-resume"
          style={{ padding: "13px 40px", borderRadius: 999, background: `linear-gradient(135deg, ${T.goldLight}, ${T.gold})`, color: "#1a0802", border: "none", cursor: "pointer", fontWeight: 900, fontSize: "0.92rem", fontFamily: "Cairo,sans-serif", boxShadow: `0 8px 32px ${T.goldGlow}` }}
        >استكشف البطولات ←</button>
      </div>
    );
  }

  const isCompleted = !!tournamentState.champion;
  const totalMatches = (tournamentState.size || 0) - 1;
  let completedMatches = 0;
  if (Array.isArray(tournamentState.rounds)) {
    tournamentState.rounds.forEach(round => {
      // rounds[i] is { name, matches } — use .matches
      (round.matches || []).forEach(match => { if (match.winner && !match.isBye) completedMatches++; });
    });
  }
  const pct = totalMatches > 0 ? Math.round((completedMatches / totalMatches) * 100) : 0;

  let currentStageName = "البطولة";
  if (Array.isArray(tournamentState.rounds) && !isCompleted) {
    for (let i = 0; i < tournamentState.rounds.length; i++) {
      const matches = tournamentState.rounds[i].matches || [];
      if (matches.some(m => !m.winner)) {
        // Use the round's own name (stored in the bracket data)
        currentStageName = tournamentState.rounds[i].name || "البطولة";
        break;
      }
    }
  }

  return (
    <div style={{ animation: "mgFadeUp 0.4s ease both" }}>
      <div className="mg-card" style={{
        position: "relative", borderRadius: 20, background: T.glass,
        border: `1.5px solid ${isCompleted ? "rgba(201,168,76,0.35)" : "rgba(201,168,76,0.28)"}`,
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        overflow: "hidden",
        boxShadow: isCompleted
          ? "0 12px 48px rgba(0,0,0,0.60), 0 0 40px rgba(201,168,76,0.08)"
          : "0 12px 48px rgba(0,0,0,0.60), 0 0 0 1px rgba(201,168,76,0.06)",
      }}>
        {/* shimmer top line */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${isCompleted ? "rgba(201,168,76,0.60)" : "rgba(201,168,76,0.45)"}, transparent)`, pointerEvents: "none" }} />

        <div style={{ padding: "22px 24px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Header row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 999, background: isCompleted ? "rgba(201,168,76,0.12)" : "rgba(20,70,44,0.55)", border: `1px solid ${isCompleted ? "rgba(201,168,76,0.28)" : T.greenBorder}`, color: isCompleted ? T.goldLight : T.green, fontSize: "0.65rem", fontWeight: 800, fontFamily: "Cairo,sans-serif", animation: "mgBadgePop 0.35s cubic-bezier(0.34,1.56,0.64,1) both" }}>
              {isCompleted ? "✦ مكتملة" : "● جارية"}
            </div>
            <div style={{ color: T.goldDim, fontSize: "0.72rem", fontWeight: 700, fontFamily: "Cairo,sans-serif" }}>
              🏆 {tournamentState.size} {tournamentState.size === 1 ? "فريق" : "فريق"}
            </div>
          </div>

          {/* Champion or stage */}
          {isCompleted ? (
            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
              <div style={{ fontSize: "2.2rem", marginBottom: 10 }}>👑</div>
              <div style={{ color: T.goldLight, fontFamily: "Cairo,sans-serif", fontWeight: 900, fontSize: "1.30rem", textShadow: `0 0 30px ${T.goldGlow}` }}>
                {tournamentState.champion}
              </div>
              <div style={{ color: T.creamDim, fontSize: "0.72rem", marginTop: 6 }}>البطل الأول</div>
            </div>
          ) : (
            <div>
              <div style={{ color: T.cream, fontFamily: "Cairo,sans-serif", fontWeight: 700, fontSize: "0.88rem", marginBottom: 12 }}>
                المرحلة الحالية: <span style={{ color: T.goldLight }}>{currentStageName}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                <span style={{ color: T.creamDim, fontSize: "0.61rem", fontFamily: "Cairo,sans-serif" }}>{completedMatches} / {totalMatches} مباراة</span>
                <span style={{ color: T.gold, fontSize: "0.61rem", fontWeight: 800 }}>{pct}%</span>
              </div>
              <div style={{ height: 3, borderRadius: 99, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 99, width: `${pct}%`, background: `linear-gradient(90deg, rgba(91,14,20,0.8), ${T.gold}, ${T.goldLight})`, boxShadow: `0 0 10px ${T.goldGlow}`, transition: "width 0.6s cubic-bezier(0.22,1,0.36,1)" }} />
              </div>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={() => navigate("/tournament/bracket")}
            className="mg-btn-resume"
            style={{
              width: "100%", padding: "13px 0", borderRadius: 14, border: "none", cursor: "pointer",
              background: `linear-gradient(135deg, rgba(201,168,76,${isCompleted ? "0.22" : "0.18"}) 0%, rgba(201,168,76,0.08) 100%)`,
              borderTop: "1px solid rgba(201,168,76,0.35)", borderLeft: "1px solid rgba(201,168,76,0.18)",
              borderRight: "1px solid rgba(201,168,76,0.18)", borderBottom: "1px solid rgba(201,168,76,0.12)",
              boxShadow: "0 4px 20px rgba(201,168,76,0.14), inset 0 1px 0 rgba(201,168,76,0.25)",
              color: T.goldLight, fontWeight: 800, fontSize: "0.88rem", fontFamily: "Cairo,sans-serif",
            }}
          >
            {isCompleted ? "🏆 عرض البطولة" : "▶ استكمال البطولة"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
export default function MyGamesPage() {
  const navigate = useNavigate();
  const {
    currentUser, userToken,
    listGameSaves, deleteGameSave, resumeGameSave,
    restoreGameState, tournamentState,
  } = useGame();

  const [saves, setSaves]               = useState([]);
  const [loadingList, setLoadingList]   = useState(true);
  const [activeTab, setActiveTab]       = useState("active");
  const [resumingId, setResumingId]     = useState(null);

  const loadSaves = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await listGameSaves();
      setSaves(data);
    } catch {
      toast.error("فشل تحميل المباريات");
    } finally {
      setLoadingList(false);
    }
  }, [listGameSaves]);

  useEffect(() => {
    if (!userToken) { navigate("/login"); return; }
    loadSaves();
  }, [userToken, loadSaves, navigate]);

  const handleResume = async (save) => {
    if (resumingId) return;
    setResumingId(save.id);
    try {
      await resumeGameSave(save.id);
      restoreGameState(save);
      if (save.current_question_state) {
        const qs = save.current_question_state;
        navigate("/question", {
          state: {
            question: qs.question, catId: qs.catId, catName: qs.catName,
            difficulty: qs.difficulty, slot: qs.slot, catIsSpecial: qs.catIsSpecial,
            turnTeam: qs.turnTeam, doubleActive: qs.doubleActive,
            _resumedRemainingTime: qs.remainingTime || 0,
            _resumedShowAnswer: qs.showAnswer || false,
          },
        });
      } else {
        navigate("/game");
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("هذه المباراة مفتوحة على جهاز آخر. أغلقها هناك ثم حاول مجدداً.");
      } else {
        toast.error(e?.response?.data?.detail || "فشل استكمال المباراة");
      }
      setResumingId(null);
    }
  };

  const handleDelete = async (saveId) => {
    try {
      await deleteGameSave(saveId);
      setSaves(prev => prev.filter(s => s.id !== saveId));
      toast.success("تم حذف المباراة");
    } catch {
      toast.error("فشل الحذف — جرب مجدداً");
    }
  };

  const activeSaves    = saves.filter(s => s.status === "active" || s.status === "paused");
  const completedSaves = saves.filter(s => s.status === "completed");
  const displayed      = activeTab === "active" ? activeSaves : completedSaves;
  const hasTournament  = !!tournamentState;

  return (
    <div style={{
      minHeight: "100svh",
      fontFamily: "'Cairo', 'Tajawal', sans-serif",
      direction: "rtl",
      color: T.cream,
      position: "relative",
      overflowX: "hidden",
    }}>
      <style>{GLOBAL_CSS}</style>

      {/* ══ BACKGROUND LAYERS ══ */}

      {/* 1 — Pinterest image, fixed */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0,
        backgroundImage: `url("${BG_IMAGE}")`,
        backgroundSize: "cover",
        backgroundPosition: "center 20%",
        backgroundAttachment: "fixed",
        filter: "brightness(0.42) saturate(0.70)",
      }} />

      {/* 2 — Burgundy-to-dark gradient */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 1,
        background:
          "linear-gradient(to bottom, rgba(20,3,6,0.60) 0%, rgba(8,2,1,0.52) 40%, rgba(4,1,0,0.78) 100%)",
      }} />

      {/* 3 — Vignette */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 2,
        background:
          "radial-gradient(ellipse 110% 90% at 50% 40%, transparent 20%, rgba(0,0,0,0.65) 100%)",
      }} />

      {/* ══ CONTENT ══ */}
      <div style={{ position: "relative", zIndex: 10, minHeight: "100svh", display: "flex", flexDirection: "column" }}>

        {/* ── Back button (no strip) ── */}
        <div style={{ padding: "18px 20px 0", animation: "mgSlideDown 0.4s ease both" }}>
          <button
            onClick={() => navigate("/")}
            style={{
              background: "rgba(201,168,76,0.06)",
              border: "1px solid rgba(201,168,76,0.22)",
              borderRadius: "13px", padding: "9px 20px",
              color: "rgba(245,237,216,0.80)", fontWeight: 700, fontSize: "0.92rem",
              cursor: "pointer", fontFamily: "Cairo,sans-serif",
              transition: "all 0.18s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = T.cream; e.currentTarget.style.borderColor = "rgba(201,168,76,0.42)"; e.currentTarget.style.background = "rgba(201,168,76,0.10)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(245,237,216,0.80)"; e.currentTarget.style.borderColor = "rgba(201,168,76,0.22)"; e.currentTarget.style.background = "rgba(201,168,76,0.06)"; }}
          >← رجوع</button>
        </div>

        {/* ── Hero ── */}
        <HeroHeader username={currentUser?.username} />

        {/* ── Tabs ── */}
        <Tabs
          active={activeTab}
          onSwitch={setActiveTab}
          counts={{ active: activeSaves.length, completed: completedSaves.length, tournaments: hasTournament ? 1 : 0 }}
        />

        {/* ── Cards grid ── */}
        <div style={{
          flex: 1,
          padding: "22px 20px 40px",
          maxWidth: "700px",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}>
          {activeTab === "tournaments" ? (
            <TournamentCard tournamentState={tournamentState} navigate={navigate} />
          ) : loadingList ? (
            <LoadingSkeleton />
          ) : displayed.length === 0 ? (
            <EmptyState tab={activeTab} onNew={() => navigate("/mode")} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {displayed.map((save, i) => (
                <GameCard
                  key={save.id}
                  save={save}
                  onResume={handleResume}
                  onDelete={handleDelete}
                  isResuming={resumingId === save.id}
                  index={i}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
